import { useEffect, useRef, useState } from 'react';

interface RevealOptions extends IntersectionObserverInit {
  /** Reveal once and stop observing (default true). */
  once?: boolean;
}

/**
 * Scroll-reveal primitive. Returns a ref to attach and a `visible` flag that
 * flips true the first time the element enters the viewport. Falls back to
 * always-visible when IntersectionObserver is unavailable (SSR / old browsers).
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(options?: RevealOptions) {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const once = options?.once ?? true;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      {
        threshold: options?.threshold ?? 0.15,
        rootMargin: options?.rootMargin ?? '0px 0px -8% 0px',
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
    // Options are intentionally not reactive — re-observing on every render is wasteful.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ref, visible };
}
